/** Shared form/state helpers used across the clinician screens. */
import type { ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { ApiError } from '../lib/api.js';
import { inputClass } from './ui.js';

export const selectClass = inputClass;

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${selectClass} ${props.className ?? ''}`} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${inputClass} ${props.className ?? ''}`} />;
}

/** A small inline error block for failed queries/mutations. */
export function ErrorState({ error, fallback }: { error: unknown; fallback: string }) {
  let message = fallback;
  let code: string | null = null;
  let details: string[] = [];
  if (error instanceof ApiError) {
    message = error.message || fallback;
    code = error.code;
    // Surface field-level validation issues ({path, message}) when present.
    if (Array.isArray(error.details)) {
      details = (error.details as { path?: (string | number)[]; instancePath?: string; message?: string }[])
        .map((d) => {
          const path = Array.isArray(d.path) ? d.path.join('.') : (d.instancePath ?? '').replace(/^\//, '');
          return path ? `${path}: ${d.message ?? 'invalid'}` : (d.message ?? '');
        })
        .filter(Boolean);
    }
  } else if (error instanceof Error && error.message) {
    message = error.message;
  }
  return (
    <div className="text-sm text-danger bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
      <div>{message}{code && code !== 'validation_error' ? ` (${code})` : ''}</div>
      {details.length > 0 && (
        <ul className="mt-1 list-disc list-inside text-xs text-red-700">
          {details.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Render the right state (loading/error/empty) for a query, else children. */
export function QueryState({
  isLoading,
  isError,
  error,
  isEmpty,
  emptyMessage,
  errorFallback,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isEmpty?: boolean;
  emptyMessage: string;
  errorFallback: string;
  children: ReactNode;
}) {
  if (isLoading) return <div className="text-slate-400 p-4">…</div>;
  if (isError) return <ErrorState error={error} fallback={errorFallback} />;
  if (isEmpty) return <div className="text-slate-400 text-sm p-4">{emptyMessage}</div>;
  return <>{children}</>;
}

/** A simple two-column grid for compact form fields. */
export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">{children}</div>;
}

/** Generate a client id for idempotent writes (check-ins, observations, questions). */
export function clientId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
